import { findIndex } from 'lodash';

import { getNodeByRefId } from './utils';

export function getScreeningBoundary(questions = []) {
  const screeningCount = questions.filter((node) => node.isScreening).length;
  const nonScreeningCount = questions.length - screeningCount;
  const lastScreeningIndex = screeningCount > 0 ? screeningCount - 1 : -1;
  const firstNonScreeningIndex = screeningCount;

  return {
    lastScreeningIndex,
    firstNonScreeningIndex,
    screeningCount,
    nonScreeningCount,
  };
}

export function shouldEnforceScreeningBoundary({
  screeningLogicEnabled = false,
  questions = [],
}) {
  if (!screeningLogicEnabled) {
    return false;
  }

  const { screeningCount, nonScreeningCount } = getScreeningBoundary(questions);

  return screeningCount > 0 && nonScreeningCount > 0;
}

export function wouldCrossScreeningBoundary({
  questions = [],
  refId,
  newIndex,
}) {
  const sourceIndex = findIndex(questions, ['refId', refId]);

  if (sourceIndex === -1 || newIndex < 0 || newIndex >= questions.length) {
    return false;
  }

  const { lastScreeningIndex, firstNonScreeningIndex } =
    getScreeningBoundary(questions);
  const isScreeningNode = questions[sourceIndex].isScreening;

  if (isScreeningNode) {
    return newIndex > lastScreeningIndex;
  }

  return newIndex < firstNonScreeningIndex;
}

function getRootInsertIndex({ questions, target, offset }) {
  if (target.groupRefId) {
    return null;
  }

  const targetIndex = findIndex(questions, ['refId', target.refId]);

  if (targetIndex === -1) {
    return null;
  }

  return targetIndex + offset;
}

export function wouldCrossScreeningBoundaryOnMove({
  questions = [],
  source,
  target,
  offset = 0,
}) {
  if (source.groupRefId || !target) {
    return false;
  }

  const sourceNode = getNodeByRefId(questions, source.refId, source.groupRefId);

  if (!sourceNode) {
    return false;
  }

  if (sourceNode.isScreening && target.groupRefId) {
    return true;
  }

  const insertIndex = getRootInsertIndex({ questions, target, offset });

  if (insertIndex === null) {
    return false;
  }

  const { lastScreeningIndex, firstNonScreeningIndex } =
    getScreeningBoundary(questions);

  if (sourceNode.isScreening) {
    return insertIndex > lastScreeningIndex;
  }

  return insertIndex < firstNonScreeningIndex;
}

export function hasScreeningNodeInMoveBatch({
  questions = [],
  nodesToMove = [],
}) {
  return nodesToMove.some((node) => {
    const resolved = getNodeByRefId(questions, node.refId, node.groupRefId);

    return resolved?.isScreening && !node.groupRefId;
  });
}

export function wouldDragCrossScreeningBoundary({
  questions = [],
  sourceRefId,
  sourceIndex,
  targetIndex,
  offset = 0,
}) {
  const newIndex =
    targetIndex > sourceIndex ? targetIndex + offset - 1 : targetIndex + offset;

  return wouldCrossScreeningBoundary({
    questions,
    refId: sourceRefId,
    newIndex,
  });
}
